<template>
    <div
        class="modal fade"
        id="armourCompendiumModal"
        tabindex="-1"
        aria-hidden="true"
        ref="modal"
    >
        <div class="modal-dialog modal-lg modal-dialog-centered">
            <div class="modal-content bg-dark text-light rounded-3 shadow-lg">
                <div class="modal-header">
                    <h4 class="modal-title">
                        {{ mode === "add" ? "Add Armour" : "Edit Armour" }}
                    </h4>
                    <button
                        type="button"
                        class="btn-close"
                        data-bs-dismiss="modal"
                    ></button>
                </div>

                <div class="modal-body">
                    <!-- Edit: pick existing -->
                    <div v-if="mode === 'edit'" class="input-group mb-3">
                        <span class="input-group-text">Select Armour</span>
                        <select
                            v-model="selectedName"
                            @change="loadArmour"
                            class="form-select"
                        >
                            <option disabled value="">Select...</option>
                            <option
                                v-for="a in store.armour.sort((a, b) =>
                                    a.name.localeCompare(b.name)
                                )"
                                :key="a.name"
                                :value="a.name"
                            >
                                {{ a.name }}
                            </option>
                        </select>
                    </div>

                    <!-- Armour form -->
                    <div class="input-group mb-3">
                        <span class="input-group-text">Name</span>
                        <input
                            v-model="armour.name"
                            type="text"
                            class="form-control"
                        />
                    </div>

                    <div class="input-group mb-3">
                        <span class="input-group-text">Description</span>
                        <textarea
                            v-model="armour.description"
                            class="form-control"
                        ></textarea>
                    </div>

                    <div class="input-group mb-3">
                        <span class="input-group-text">Armour Slots</span>
                        <select v-model="armour.armourSlots" class="form-select">
                            <option disabled value="">Select...</option>
                            <option v-for="n in 3" :key="n" :value="n">
                                {{ n }}
                            </option>
                        </select>
                    </div>

                    <!-- Abilities -->
                    <div class="input-group mb-3">
                        <span class="input-group-text">Ability</span>
                        <select v-model="selectedAbility" class="form-select">
                            <option disabled value="">Select...</option>
                            <option
                                v-for="a in store.armourAbilities.sort((a, b) =>
                                    a.name.localeCompare(b.name)
                                )"
                                :key="a.name"
                                :value="a.name"
                            >
                                {{ a.name }}
                            </option>
                        </select>
                        <button
                            class="btn btn-outline-primary"
                            type="button"
                            @click="addAbility"
                        >
                            Add
                        </button>
                    </div>

                    <div class="input-group mb-3">
                        <span class="input-group-text">Custom Ability</span>
                        <input
                            v-model="customAbilityName"
                            type="text"
                            class="form-control"
                            placeholder="Name"
                        />
                        <input
                            v-model="customAbilityDesc"
                            type="text"
                            class="form-control"
                            placeholder="Description"
                        />
                        <button
                            class="btn btn-outline-primary"
                            type="button"
                            @click="addCustomAbility"
                        >
                            Add
                        </button>
                    </div>

                    <!-- Selected Abilities -->
                    <div
                        v-if="armour.abilities.length"
                        class="d-flex flex-wrap gap-2"
                    >
                        <span
                            v-for="(ab, i) in armour.abilities"
                            :key="i"
                            class="badge bg-success d-flex align-items-center"
                        >
                            {{ typeof ab === "object" ? ab.name : ab }}
                            <button
                                type="button"
                                class="btn-close btn-close-white ms-2"
                                style="font-size: 0.6rem"
                                @click="removeAbility(i)"
                            ></button>
                        </span>
                    </div>
                </div>

                <p class="px-3">
                    <strong
                        >Note: These changes won't affect any armour currently
                        added to your character. You will need to remove and
                        re-add the armour for the changes to take
                        effect.</strong
                    >
                </p>

                <div class="modal-footer">
                    <button class="btn btn-secondary" data-bs-dismiss="modal">
                        <font-awesome-icon icon="multiply" class="me-1" />Close
                    </button>
                    <button
                        v-if="mode === 'edit'"
                        class="btn btn-danger me-1"
                        @click="deleteArmour"
                    >
                        <font-awesome-icon
                            icon="trash-alt"
                            class="me-1"
                        />Delete
                    </button>
                    <button class="btn btn-primary" @click="saveArmour">
                        <font-awesome-icon icon="save" class="me-1" />Save
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { useRosterStore } from "@/stores/index";
import { normalizeArmour } from "@/utils/armour";

export default {
    name: "ArmourCompendiumModal",
    props: { mode: { type: String, default: "add" } },
    data() {
        return {
            store: useRosterStore(),
            selectedName: "",
            armour: { name: "", armourSlots: "", description: "", abilities: [] },
            selectedAbility: "",
            customAbilityName: "",
            customAbilityDesc: "",
        };
    },
    mounted() {
        this.$refs.modal.addEventListener("show.bs.modal", () => {
            this.resetForm();
        });
    },
    methods: {
        resetForm() {
            this.selectedName = "";
            this.armour = {
                name: "",
                armourSlots: "",
                description: "",
                abilities: [],
            };
            this.selectedAbility = "";
            this.customAbilityName = "";
            this.customAbilityDesc = "";
        },
        loadArmour() {
            const found = this.store.armour.find(
                (a) => a.name === this.selectedName
            );
            if (found) this.armour = normalizeArmour(JSON.parse(JSON.stringify(found)));
        },
        ensureUniqueAbilityName(name) {
            let newName = name;
            let counter = 65; // ASCII A
            while (this.store.armourAbilities.some((a) => a.name === newName)) {
                newName = `${name} (${String.fromCharCode(counter)})`;
                counter++;
            }
            return newName;
        },
        saveArmour() {
            if (!this.armour.name.trim()) {
                alert("Armour must have a name!");
                return;
            }
            if (!this.armour.description.trim()) {
                alert("Armour must have a description!");
                return;
            }
            if (!this.armour.armourSlots || Number(this.armour.armourSlots) < 1 || Number(this.armour.armourSlots) > 3) {
                alert("Armour must have Armour Slots between 1 and 3!");
                return;
            }
            if (
                this.customAbilityName.trim() ||
                this.customAbilityDesc.trim()
            ) {
                alert(
                    "You typed a custom ability but didn’t add it. Please press Add first."
                );
                return;
            }
            if (this.selectedAbility !== "") {
                alert(
                    "You selected an ability but didn’t add it. Please press Add first."
                );
                return;
            }

            // ✅ Preserve description when promoting custom abilities to the compendium
            for (const ability of this.armour.abilities) {
                if (ability.custom) {
                    const uniqueName = this.ensureUniqueAbilityName(
                        ability.name
                    );
                    const abilityToAdd = {
                        name: uniqueName,
                        description: ability.description, // <-- keep it!
                    };
                    this.store.armourAbilities.push(abilityToAdd);

                    // Also update the local ability name so it matches the compendium entry
                    ability.name = uniqueName;
                    ability.custom = false;
                }
            }

            this.armour = normalizeArmour(this.armour);

            // Your armour items store just names (that’s fine)
            this.armour.abilities = this.armour.abilities.map((a) =>
                typeof a === "string" ? a : a.name
            );

            if (this.mode === "add") {
                this.store.armour.push(this.armour);
                alert("Armour added!");
            } else {
                const idx = this.store.armour.findIndex(
                    (a) => a.name === this.selectedName
                );
                if (idx !== -1) this.store.armour.splice(idx, 1, this.armour);
                alert("Armour updated!");
            }
            this.resetForm();
        },
        deleteArmour() {
            if (!this.selectedName) return;
            if (confirm(`Delete armour "${this.selectedName}"?`)) {
                const idx = this.store.armour.findIndex(
                    (a) => a.name === this.selectedName
                );
                if (idx !== -1) this.store.armour.splice(idx, 1);
            }
            this.resetForm();
            alert("Armour deleted!");
        },
        addAbility() {
            if (!this.selectedAbility) return;
            const ab = this.store.armourAbilities.find(
                (a) => a.name === this.selectedAbility
            );
            if (!ab) return;

            const exists = this.armour.abilities.some(
                (a) => (typeof a === "string" ? a : a.name) === ab.name
            );
            if (!exists) {
                this.armour.abilities.push({ ...ab, custom: false }); // keep description here too
            }
            this.selectedAbility = "";
        },
        addCustomAbility() {
            if (
                !this.customAbilityName.trim() ||
                !this.customAbilityDesc.trim()
            ) {
                alert("Custom ability must have both Name and Description!");
                return;
            }
            this.armour.abilities.push({
                name: this.customAbilityName.trim(),
                description: this.customAbilityDesc.trim(),
                custom: true,
            });
            this.customAbilityName = "";
            this.customAbilityDesc = "";
        },
        removeAbility(i) {
            this.armour.abilities.splice(i, 1);
        },
    },
};
</script>

